import { Link, useRouterState, useNavigate, useRouter } from "@tanstack/react-router";
import { Shield, LayoutDashboard, Search, Calculator, HeartPulse, Sparkles, Settings, Menu, X, LogOut, Crown, TrendingUp, HandCoins, Store, Wallet, IdCard, ExternalLink } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Footer } from "@/components/Footer";
import { createPortalSession } from "@/utils/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";

const baseNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/invest", label: "Invest", icon: TrendingUp },
  { to: "/borrow", label: "Borrow", icon: HandCoins },
  { to: "/marketplace", label: "Marketplace", icon: Store },
  { to: "/wallet", label: "Wallet", icon: Wallet },
  { to: "/kyc", label: "KYC", icon: IdCard },
  { to: "/lenders", label: "Lender Search", icon: Search },
  { to: "/analyzer", label: "Loan Analyzer", icon: Calculator },
  { to: "/health", label: "Financial Health", icon: HeartPulse },
  { to: "/coach", label: "AI Coach", icon: Sparkles },
];

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { isPremium } = useSubscription();
  const { isAdmin } = useIsAdmin();
  const nav = isAdmin ? [...baseNav, { to: "/admin", label: "Admin", icon: Settings }] : baseNav;
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    setPortalLoading(true);
    const result = await createPortalSession({
      data: { environment: getStripeEnvironment(), returnUrl: window.location.href },
    });
    setPortalLoading(false);
    if ("error" in result) {
      toast.error(result.error);
      return;
    }
    window.open(result.url, "_blank");
  };

  const handleSignOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await signOut();
    await router.invalidate();
    navigate({ to: "/auth", replace: true });
  };

  const initials = (user?.user_metadata?.full_name || user?.email || "?")
    .split(/[ @]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s: string) => s[0]?.toUpperCase() ?? "")
    .join("") || "?";

  const greeting = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "guest";

  return (
    <div className="flex min-h-screen bg-gradient-surface">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-64 flex-col transform border-r border-border bg-card transition-transform lg:static lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-16 items-center gap-2 border-b border-border px-5">
          <Link to="/" className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-hero text-primary-foreground shadow-soft">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-base font-semibold tracking-tight">LendyTrack</div>
              <div className="-mt-1 text-[10px] uppercase tracking-[0.18em] text-accent">AI</div>
            </div>
          </Link>
        </div>
        <nav className="flex flex-1 flex-col gap-1 p-3">
          {nav.map((item) => {
            const Icon = item.icon;
            const active = pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  active
                    ? "bg-primary text-primary-foreground shadow-soft"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        {isPremium ? (
          <div className="mx-3 mb-4 rounded-xl border border-accent/30 bg-accent/10 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-accent">
              <Crown className="h-3 w-3" /> Premium
            </div>
            <div className="mt-1 text-xs text-muted-foreground">All features unlocked.</div>
            <button
              onClick={openPortal}
              disabled={portalLoading}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
            >
              {portalLoading ? "Opening portal…" : "Manage plan"} <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <div className="mx-3 mb-4 rounded-xl border border-border bg-gradient-hero p-4 text-primary-foreground">
            <div className="text-xs uppercase tracking-widest opacity-70">Free plan</div>
            <div className="mt-1 text-sm font-medium">Unlock unlimited coaching, full analyzer & saved analyses.</div>
            <Link to="/pricing" className="mt-3 inline-block rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-primary shadow-soft hover:opacity-90">
              Upgrade
            </Link>
          </div>
        )}
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card/80 px-4 backdrop-blur lg:px-8">
          <button
            className="lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="hidden text-sm text-muted-foreground lg:block">
            {user ? <>Welcome back, <span className="font-medium text-foreground">{greeting}</span></> : <Link to="/auth" className="font-medium text-primary">Sign in</Link>}
          </div>
          <div className="flex items-center gap-3">
            {!isPremium && (
              <Link
                to="/pricing"
                className="hidden rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-primary shadow-soft hover:opacity-90 sm:inline-flex sm:items-center sm:gap-1"
              >
                <Crown className="h-3 w-3" /> Upgrade
              </Link>
            )}
            {user ? (
              <>
                <div className="grid h-9 w-9 place-items-center rounded-full bg-primary text-xs font-semibold text-primary-foreground" title={user.email ?? ""}>
                  {initials}
                </div>
                <button
                  onClick={handleSignOut}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Sign out"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <Link to="/auth" className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90">
                Sign in
              </Link>
            )}
          </div>
        </header>
        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-10">{children}</main>
        <Footer />
      </div>
    </div>
  );
}
